import React from "react";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";
import { DotsThree } from "@phosphor-icons/react";

import DomainPill from "components/ClientDetails/Sections/GoalsAndTargets/shared/DomainPill";
import StatusPill from "components/ClientDetails/Sections/GoalsAndTargets/shared/StatusPill";
import type {
  BehaviorSubject,
  GoalType,
} from "components/ClientDetails/Sections/GoalsAndTargets/shared/types";
import type { Status as StatusId, GoalDomain as DomainId } from "api/types/aba";
import MasteryBanner, { MasteryBannerLevel } from "../../MasteryBanner";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
export interface GoalCardProps {
  /** Display name of the goal */
  goalName: string;
  /** Current status of the goal */
  status: StatusId;
  /** Clinical domain of the goal */
  domain: DomainId;
  /** Whose behavior is being measured */
  behaviorMeasured: BehaviorSubject;
  /** Goal type: skill acquisition or behavior reduction */
  goalType: GoalType;
  /** Number of targets under this goal */
  targetCount: number;
  /**
   * Slot for injecting a sparkline or trend chart.
   * The parent is responsible for providing the chart implementation.
   */
  chartSlot?: React.ReactNode;
  /** Optional mastery signal level — renders MasteryBanner when provided */
  masteryBannerLevel?: MasteryBannerLevel;
  /** Title text for the mastery banner */
  masteryBannerTitle?: string;
  /** Supporting context text shown inside the mastery banner */
  masteryBannerDetail?: string;
  /** Called when the user dismisses the mastery banner */
  onDismissMasteryBanner?: () => void;
  /** Called when the overflow menu button is clicked */
  onMenuOpen?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /** Called when the card body is clicked — use to navigate to goal detail */
  onClick?: () => void;
  /** Additional MUI sx overrides for the outer card container */
  sx?: SxProps<Theme>;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const GOAL_TYPE_LABELS: Record<GoalType, string> = {
  skillAcquisition: "Skill Acquisition",
  behaviorReduction: "Behavior Reduction",
};

const BEHAVIOR_SUBJECT_LABELS: Record<BehaviorSubject, string> = {
  client: "Client",
  caregiver: "Caregiver",
};

// ---------------------------------------------------------------------------
// Internal sub-components
// ---------------------------------------------------------------------------
interface MetaRowProps {
  label: string;
  children: React.ReactNode;
}

function MetaRow({ label, children }: MetaRowProps) {
  return (
    <Box
      component="dd"
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      gap={3}
      width="100%"
      m={0}
    >
      <Typography variant="body2" color="text.secondary" noWrap>
        {label}
      </Typography>
      {children}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// GoalCard
// ---------------------------------------------------------------------------

/**
 * Summary card for a single goal in the goal list view.
 * Displays the goal name, status, trend chart, optional mastery alert,
 * and key metadata (domain, behavior subject, type, target count).
 *
 * @param props
 * @param props.goalName               - Display name of the goal
 * @param props.status                 - Current goal status
 * @param props.domain                 - Clinical domain
 * @param props.behaviorMeasured       - Whose behavior is tracked
 * @param props.goalType               - Skill acquisition or behavior reduction
 * @param props.targetCount            - Number of targets under this goal
 * @param props.chartSlot              - Slot for a sparkline / trend chart node
 * @param props.masteryBannerLevel     - Optional mastery signal (renders MasteryBanner)
 * @param props.masteryBannerDetail    - Supporting text for the mastery banner
 * @param props.onDismissMasteryBanner - Callback to dismiss the mastery banner
 * @param props.onMenuOpen             - Callback for the overflow menu button
 * @param props.onClick                - Callback to navigate to goal detail
 * @param props.sx                     - Additional MUI sx overrides
 */
const GoalCard: React.FC<GoalCardProps> = ({
  goalName,
  status,
  domain,
  behaviorMeasured,
  goalType,
  targetCount,
  chartSlot,
  masteryBannerLevel,
  masteryBannerTitle,
  masteryBannerDetail,
  onDismissMasteryBanner,
  onMenuOpen,
  onClick,
  sx,
}) => {
  return (
    <Stack
      direction="column"
      gap={3}
      component="article"
      p={3}
      aria-label={goalName}
      height={"100%"}
      sx={[
        (theme) => ({
          borderRadius: 3,
          bgcolor: (theme.palette as any).grayscale?.[50] ?? "grey.50",
          width: "100%",
          border: "1px solid transparent",
          transition: "border-color 0.2s",
          "&:hover": {
            borderColor: "primary.main",
            cursor: onClick ? "pointer" : "default",
          },
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      onClick={onClick}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 1, // 4px — aligns with inner card padding
          gap: 1,
        }}
      >
        <Typography component="h3" variant="subtitle2" color="text.primary">
          {goalName}
        </Typography>

        <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
          <StatusPill status={status} />
          <IconButton
            size="small"
            aria-label="Goal options"
            onClick={(e) => {
              e.stopPropagation();
              onMenuOpen?.(e);
            }}
          >
            <DotsThree size={20} weight="bold" />
          </IconButton>
        </Stack>
      </Box>

      {/* ── Inner card ── */}
      <Box
        component="section"
        aria-label="Goal details"
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 5, // 16px
          flex: 1,
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: "12px", // design spec value — no matching token
          p: 5, // 16px (design is 17px; spacing(5)=16px is the closest token)
          width: "100%",
        }}
      >
        {/* Chart slot */}
        {chartSlot && (
          <Box aria-label="Goal trend chart" sx={{ width: "100%", flex: 1, minHeight: 0 }}>
            {chartSlot}
          </Box>
        )}

        {/* Mastery banner (optional) */}
        {masteryBannerLevel && (
          <MasteryBanner
            level={masteryBannerLevel}
            title={masteryBannerTitle}
            detail={masteryBannerDetail}
            onDismiss={onDismissMasteryBanner}
          />
        )}

        {/* Meta rows */}
        <Stack
          direction="column"
          gap={2}
          component="dl"
          aria-label="Goal metadata"
          width="100%"
          m={0}
        >
          <MetaRow label="Domain">
            <DomainPill domain={domain} />
          </MetaRow>

          <MetaRow label="Behavior measured">
            <Typography variant="body2" color="text.primary">
              {BEHAVIOR_SUBJECT_LABELS[behaviorMeasured]}
            </Typography>
          </MetaRow>

          <MetaRow label="Type">
            <Typography variant="body2" color="text.primary">
              {GOAL_TYPE_LABELS[goalType]}
            </Typography>
          </MetaRow>

          <MetaRow label="Targets">
            <Typography variant="body2" color="text.primary">
              {targetCount}
            </Typography>
          </MetaRow>
        </Stack>
      </Box>
    </Stack>
  );
};

export default GoalCard;
